import { useState, useRef, useMemo } from 'react';
import type { ExtendedCertificate, CertificateDesignConfig } from 'shared';
import { DETECTIVE_TYPES, FONT_CLASS_MAP, formatPlayTime } from 'shared';
import { rarityGlyph } from '../../utils/endingGlyph';
import { RadarChart } from './RadarChart';

interface DetailedReportProps {
  certificate: ExtendedCertificate;
  designConfig?: CertificateDesignConfig;
  onClose?: () => void;
  onShare?: () => void;
}

export function DetailedReport({
  certificate,
  designConfig,
  onClose,
  onShare
}: DetailedReportProps) {
  const [isSharing, setIsSharing] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const { detailedReport } = certificate;
  const typeInfo = DETECTIVE_TYPES[detailedReport.detectiveType];

  const handleShare = async () => {
    setIsSharing(true);
    try {
      const shareUrl = `${window.location.origin}/certificate/${certificate.shareToken}`;
      // P8-3: ハッシュタグを「#九十九商会」先頭の1系統へ統一（CompletionCertificate と共通）。
      const shareText = `【${typeInfo?.name ?? '探偵'}】『${certificate.scenarioTitle}』の解決に協力しました。\n${typeInfo?.shortDescription ?? ''}\n\n#九十九商会 #謎解きゲーム #探偵診断`;

      if (navigator.share) {
        await navigator.share({
          title: `解決協力報告書 - ${certificate.scenarioTitle}`,
          text: shareText,
          url: shareUrl
        });
      } else {
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        window.open(twitterUrl, '_blank', 'width=550,height=420');
      }
      onShare?.();
    } catch {
      // ユーザーがキャンセルした場合
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyUrl = async () => {
    try {
      const shareUrl = `${window.location.origin}/certificate/${certificate.shareToken}`;
      await navigator.clipboard.writeText(shareUrl);
      alert('共有リンクをコピーしました');
    } catch {
      alert('リンクをコピーできませんでした');
    }
  };

  // P8-3: rarity の表記は図鑑と同一の「常/珍/稀/伝」（SSoT: utils/endingGlyph.ts）。
  // P8-2a: 色も図鑑と単一パレット化（SSoT: styles/shell-tokens.css の --shell-rarity-*。
  // 旧 #22c55e/#3b82f6/#eab308 の完了書ローカルパレットを廃止）。
  const rarityColors: Record<string, string> = {
    common: 'var(--shell-rarity-common)',
    uncommon: 'var(--shell-rarity-uncommon)',
    rare: 'var(--shell-rarity-rare)',
    legendary: 'var(--shell-rarity-legendary)'
  };

  // P8-2b: grade 別の Web 色（S=#eab308/A=#22c55e/…の inline backgroundColor）を廃止。
  // 等級章は金印章の単一意匠（CSS .report-card__grade）で描き、等級は文字で語る。
  const { tsukumogamiEvaluation } = detailedReport;

  // デザイン設定のクラス名とスタイルを計算
  const { cardClassName, cardStyle, commentStyle, artifactCommentStyle, commentFontClass, artifactFontClass } = useMemo(() => {
    const classes = ['report-card'];
    const style: React.CSSProperties = {};

    if (designConfig) {
      // テーマ
      if (designConfig.theme === 'paper') {
        classes.push('report-card--paper');
      }

      // レーダーチャートスタイル
      if (designConfig.radarChartStyle === 'solid') {
        classes.push('report-card--radar-solid');
      } else if (designConfig.radarChartStyle === 'sketch') {
        classes.push('report-card--radar-sketch');
      }

      // 背景画像
      if (designConfig.backgroundImageUrl) {
        classes.push('has-bg-image');
        style.backgroundImage = `url(${designConfig.backgroundImageUrl})`;
      }
    }

    // 評価者コメントスタイル
    const evalStyle: React.CSSProperties = {};
    const evalFontClass = designConfig?.evaluatorCommentStyle?.fontFamily
      ? FONT_CLASS_MAP[designConfig.evaluatorCommentStyle.fontFamily] ?? ''
      : '';
    if (designConfig?.evaluatorCommentStyle) {
      const { fontSize, lineHeight, color } = designConfig.evaluatorCommentStyle;
      if (fontSize) evalStyle.fontSize = `${fontSize}rem`;
      if (lineHeight) evalStyle.lineHeight = lineHeight;
      if (color) evalStyle.color = color;
    }

    // アーティファクトコメントスタイル
    const artStyle: React.CSSProperties = {};
    const artFontClass = designConfig?.artifactCommentStyle?.fontFamily
      ? FONT_CLASS_MAP[designConfig.artifactCommentStyle.fontFamily] ?? ''
      : '';
    if (designConfig?.artifactCommentStyle) {
      const { fontSize, lineHeight, color } = designConfig.artifactCommentStyle;
      if (fontSize) artStyle.fontSize = `${fontSize}rem`;
      if (lineHeight) artStyle.lineHeight = lineHeight;
      if (color) artStyle.color = color;
    }

    return {
      cardClassName: classes.join(' '),
      cardStyle: style,
      commentStyle: evalStyle,
      artifactCommentStyle: artStyle,
      commentFontClass: evalFontClass,
      artifactFontClass: artFontClass
    };
  }, [designConfig]);

  // 評価者の顔写真URL（優先順位: detailedReport > designConfig）
  const evaluatorPortraitUrl = tsukumogamiEvaluation.evaluatorPortraitUrl
    ?? designConfig?.evaluatorDisplay?.portraitUrl;
  const showPortrait = designConfig?.evaluatorDisplay?.showPortrait && evaluatorPortraitUrl;

  return (
    <div className="report-overlay">
      <div className="report-scroll-container">
        {/* 9:16レポートカード */}
        <div className={cardClassName} style={cardStyle} ref={reportRef}>
          {/* ヘッダー */}
          <div className="report-card__header">
            <span className="report-card__badge">九十九商会 解決協力報告書</span>
            <h2 className="report-card__scenario">{certificate.scenarioTitle}</h2>
          </div>

          {/* 上部セクション: 左（タイプ情報）+ 右（チャート） */}
          <div className="report-card__top-section">
            {/* 左カラム: タイプ情報・説明・ステータス・強み */}
            <div className="report-card__left-column">
              {/* タイプセクション */}
              <div className="report-card__type-section">
                <span
                  className="report-card__rarity"
                  style={{ color: rarityColors[detailedReport.rarity] }}
                >
                  {rarityGlyph(detailedReport.rarity)}
                </span>
                <span className="report-card__type-code">{detailedReport.detectiveType}</span>
                <h3 className="report-card__type-name">{typeInfo?.name ?? '不明'}</h3>
                <p className="report-card__type-title">{typeInfo?.title}</p>
              </div>

              {/* タイプ説明 */}
              <p className="report-card__description">{typeInfo?.shortDescription}</p>

              {/* 行動分析バー */}
              <div className="report-card__stats">
                <div className="report-card__stat-row">
                  <span className="report-card__stat-label">リスク選好</span>
                  <div className="report-card__stat-bar">
                    <div
                      className="report-card__stat-fill"
                      style={{ width: `${detailedReport.behaviorAnalysis.choiceTendency.riskTaking}%` }}
                    />
                  </div>
                  <span className="report-card__stat-value">{detailedReport.behaviorAnalysis.choiceTendency.riskTaking}</span>
                </div>
                <div className="report-card__stat-row">
                  <span className="report-card__stat-label">共感重視</span>
                  <div className="report-card__stat-bar">
                    <div
                      className="report-card__stat-fill"
                      style={{ width: `${detailedReport.behaviorAnalysis.choiceTendency.empathyFocus}%` }}
                    />
                  </div>
                  <span className="report-card__stat-value">{detailedReport.behaviorAnalysis.choiceTendency.empathyFocus}</span>
                </div>
                <div className="report-card__stat-row">
                  <span className="report-card__stat-label">徹底度</span>
                  <div className="report-card__stat-bar">
                    <div
                      className="report-card__stat-fill"
                      style={{ width: `${detailedReport.behaviorAnalysis.choiceTendency.thoroughness}%` }}
                    />
                  </div>
                  <span className="report-card__stat-value">{detailedReport.behaviorAnalysis.choiceTendency.thoroughness}</span>
                </div>
              </div>

              {/* 強み */}
              <div className="report-card__strengths">
                <h4 className="report-card__section-title">発揮された強み</h4>
                <ul className="report-card__strength-list">
                  {detailedReport.demonstratedStrengths.slice(0, 2).map((s, i) => (
                    <li key={i}>✓ {s}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 右カラム: レーダーチャート */}
            <div className="report-card__right-column">
              <RadarChart params={certificate.params} size={140} showLabels={true} />
            </div>
          </div>

          {/* 担当からのメッセージ（拡張エリア） */}
          <div className="report-card__message-section">
            <div className={`report-card__message-header${showPortrait ? ' report-card__message-header-with-portrait' : ''}`}>
              {showPortrait && (
                <img
                  src={evaluatorPortraitUrl}
                  alt={tsukumogamiEvaluation.evaluatorName}
                  className="report-card__evaluator-portrait"
                />
              )}
              <span className="report-card__message-from">{tsukumogamiEvaluation.evaluatorName}より</span>
              <div className="report-card__grade">
                {tsukumogamiEvaluation.overallGrade}
              </div>
            </div>
            <p
              className={`report-card__message-body ${commentFontClass}`.trim()}
              style={commentStyle}
            >
              「{tsukumogamiEvaluation.comment}」
            </p>

            {/* TrueEnd限定: アーティファクトからのひと言 */}
            {certificate.endingType === 'true' && tsukumogamiEvaluation.artifactComment && (
              <div className="report-card__artifact-inline">
                <span className="report-card__artifact-from">{tsukumogamiEvaluation.artifactComment.name}より</span>
                <p
                  className={`report-card__artifact-body ${artifactFontClass}`.trim()}
                  style={artifactCommentStyle}
                >
                  「{tsukumogamiEvaluation.artifactComment.message}」
                </p>
              </div>
            )}
          </div>

          {/* メタ情報 */}
          <div className="report-card__meta">
            <div className="report-card__meta-item">
              <span className="report-card__meta-label">プレイ時間</span>
              <span className="report-card__meta-value">{formatPlayTime(certificate.playTimeMs)}</span>
            </div>
            <div className="report-card__meta-item">
              <span className="report-card__meta-label">達成日</span>
              <span className="report-card__meta-value">
                {new Date(certificate.completedAt).toLocaleDateString('ja-JP')}
              </span>
            </div>
            <div className="report-card__meta-item">
              <span className="report-card__meta-label">相性の良い付喪神</span>
              <span className="report-card__meta-value">{typeInfo?.tsukumogamiAffinity}</span>
            </div>
          </div>

          {/* フッター */}
          <div className="report-card__footer">
            <span className="report-card__hashtag">#九十九商会 #探偵診断</span>
          </div>
        </div>

        {/* 操作ボタン（カード外） */}
        <div className="report-actions">
          <button
            className="report-actions__btn report-actions__btn--share"
            onClick={handleShare}
            disabled={isSharing}
          >
            {isSharing ? '処理中……' : '共有する'}
          </button>
          <button
            className="report-actions__btn report-actions__btn--copy"
            onClick={handleCopyUrl}
          >
            リンクをコピー
          </button>
          {onClose && (
            <button
              className="report-actions__btn report-actions__btn--close"
              onClick={onClose}
            >
              閉じる
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default DetailedReport;
